import React from 'react';
import { Box, Typography, Grid, Chip } from '@mui/material';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';

import {
  TOKENS,
  fmt,
  fmtMoney,
  ticketGameColor,
  bonusGameColor,
  pointTier,
  shiftDurationLabel,
  formatHoursWorked,
} from '../theme';
import ManagerModificationsList from './ManagerModificationsList';
import { enrichTxnDescription } from './auditFormat';

// Mirrors ShiftDetail.isCashLeftTo — only the sender's "CASH LEFT TO X"
// row is hidden from the Bank table. The receiver's "CASH LEFT FROM X"
// row is real bank intake and stays.
function isCashLeftTo(row) {
  const d = row?.description?.toUpperCase() || '';
  return d.startsWith('CASH LEFT TO');
}

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || '';

// Route an upload-image URL through the Grails /image/proxy endpoint so
// the response carries the Access-Control-Allow-Origin header html2canvas
// needs to read pixels. The proxy resolves the path under KINGDOM_HOME on
// the server.
//
// We also ask the proxy to downscale the image to PDF_IMAGE_WIDTH px and
// re-encode as JPEG. Bonus photos can ship at 5-10MB each at full camera
// resolution; pulling them down to ~600px wide keeps the captured canvas
// (and the resulting PDF) from blowing up to tens of megabytes. The
// printed A4 column is only ~94mm wide, so 600px is already overkill for
// print quality.
const PDF_IMAGE_WIDTH = 600;

function pdfSrc(url) {
  if (!url) return url;
  let relativePath;
  try {
    const u = new URL(url, window.location.origin);
    relativePath = u.pathname.replace(/^\/+/, '');
  } catch (e) {
    relativePath = String(url).replace(/^\/+/, '');
  }
  return `${API_ENDPOINT}/image/proxy?path=${encodeURIComponent(
    relativePath
  )}&w=${PDF_IMAGE_WIDTH}`;
}

// Render the raw clock-record value (e.g. "5/24/2026 8:30 AM") in a single
// compact line. Empty / unset → em-dash so the cell doesn't collapse.
function dateTime(raw) {
  if (!raw) return '—';
  return String(raw).toUpperCase();
}

// Rich per-report layout — captured by html2canvas in generateSessionPdf
// and embedded into a real PDF. Sized for a 720×932 logical document.
export default function PdfBody({
  type,
  session,
  sessionData,
  locationName,
  customers,
  employees,
  manageBonusList,
}) {
  const data = sessionData || {};
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <PdfHeader type={type} session={session} locationName={locationName} />
      <Box sx={{ flex: 1 }}>
        {type === 'shift' && (
          <ShiftPdf
            data={data}
            customers={customers}
            employees={employees}
            manageBonusList={manageBonusList}
          />
        )}
        {type === 'bonus' && <BonusPdf data={data} />}
        {type === 'raffle' && <RafflePdf data={data} />}
        {type === 'points' && <PointsPdf data={data} />}
        {type === 'ticket' && <TicketPdf data={data} />}
      </Box>
    </Box>
  );
}

function PdfHeader({ type, session, locationName }) {
  // Shift report has wider tables, so allow the location name to wrap; other
  // reports keep it on a single noWrap line.
  const allowWrap = type === 'shift';
  return (
    <Box
      data-pdf-block="header"
      sx={{
        border: `1px solid ${TOKENS.slate200}`,
        borderRadius: 1,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Grid container alignItems="center" spacing={1}>
        <Grid item xs={3}>
          <Typography
            sx={{
              fontSize: allowWrap ? 15 : 17,
              fontWeight: 700,
              lineHeight: 1.15,
              wordBreak: 'break-word',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: allowWrap ? 2 : 1,
              overflow: 'hidden',
            }}
          >
            {locationName || ''}
          </Typography>
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
              color: TOKENS.blue700,
              mt: 0.25,
            }}
          >
            {(type || '').toUpperCase()} REPORT
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <MetaCell label="Session" value={`#${session?.id || ''}`} />
        </Grid>
        <Grid item xs={2}>
          <MetaCell label="Employee" value={session?.employee || ''} />
        </Grid>
        <Grid item xs={3}>
          <MetaCell
            label="Shift"
            lines={[
              `IN  ${dateTime(session?.clockIn)}`,
              `OUT ${dateTime(session?.clockOut)}`,
              `HRS ${
                formatHoursWorked(session?.hoursWorked) ||
                shiftDurationLabel(session) ||
                '—'
              }`,
            ]}
          />
        </Grid>
        <Grid item xs={2}>
          <MetaCell label="Assigned Day" value={session?.assignedDay || ''} />
        </Grid>
      </Grid>
    </Box>
  );
}

function MetaCell({ label, value, lines }) {
  const items = Array.isArray(lines) ? lines : [value];
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography
        sx={{
          fontSize: 9,
          fontWeight: 700,
          textTransform: 'uppercase',
          color: TOKENS.slate400,
          letterSpacing: '0.05em',
        }}
      >
        {label}
      </Typography>
      {items.map((v, i) => (
        <Typography
          key={i}
          sx={{
            fontSize: 10,
            fontWeight: 700,
            color: TOKENS.slate900,
            fontFamily: 'monospace',
            lineHeight: 1.3,
            letterSpacing: '-0.01em',
          }}
          noWrap
        >
          {v}
        </Typography>
      ))}
    </Box>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <Box
      sx={{
        border: `1px solid ${accent}`,
        borderRadius: 1,
        p: 1.25,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography
        sx={{
          fontSize: 9,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: TOKENS.slate500,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{ fontSize: 18, fontWeight: 700, fontFamily: 'monospace' }}
      >
        {value}
      </Typography>
    </Box>
  );
}

// ── SHIFT ────────────────────────────────────────────────────────────────
function ShiftPdf({ data, customers, employees, manageBonusList }) {
  const bank = (data.bank || []).filter(
    (r) => r.entryStatus !== 'deleted' && !isCashLeftTo(r)
  );
  const expenses = (data.expenses || []).filter(
    (r) => r.entryStatus !== 'deleted'
  );
  const bonuses = (data.bonuses || []).filter((b) => b.status !== 'deleted');
  const points = data.points || [];
  const raffles = (data.raffles || []).filter((r) => r.status !== 'deleted');
  const ticketOuts = (data.ticketOuts || []).filter(
    (t) => t.status !== 'deleted'
  );

  const totalBank = bank.reduce((s, r) => s + Number(r.amount || 0), 0);
  const totalExpenses = expenses.reduce((s, r) => s + Number(r.amount || 0), 0);
  const totalBonus = bonuses.reduce(
    (s, b) => s + Number(b.bonusAmount || 0),
    0
  );
  const totalPoints = points.reduce((s, p) => s + Number(p.points || 0), 0);
  const totalRaffles = raffles.reduce((s, r) => s + Number(r.amount || 0), 0);
  const totalTickets = ticketOuts.reduce(
    (s, t) => s + Number(t.ticketOut || 0),
    0
  );

  const totalOutgoing =
    totalExpenses + totalBonus + totalPoints + totalTickets + totalRaffles;
  const closingCash = Number(data.cashLeft || 0);
  const difference = totalBank - totalOutgoing;
  const shortOver = closingCash - difference;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Grid container spacing={1} data-pdf-block="kpis">
        <Grid item xs={3}>
          <Kpi
            label="Total Bank"
            value={fmt(totalBank)}
            accent={TOKENS.emerald200}
          />
        </Grid>
        <Grid item xs={3}>
          <Kpi
            label="Total Expenses"
            value={fmt(totalOutgoing)}
            accent={TOKENS.red200}
          />
        </Grid>
        <Grid item xs={3}>
          <Kpi
            label="Short / Over"
            value={`${shortOver > 0 ? '+' : ''}${fmt(shortOver)}`}
            accent={shortOver < 0 ? TOKENS.red200 : TOKENS.emerald200}
          />
        </Grid>
        <Grid item xs={3}>
          <Kpi
            label="Closing Cash"
            value={fmt(closingCash)}
            accent={TOKENS.blue200}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} data-pdf-block="txn-tables">
        <Grid item xs={6}>
          <TxnTable
            rows={bank}
            amountKey="amount"
            label="Bank (+)"
            total={totalBank}
            bg={TOKENS.emerald50}
            border={TOKENS.emerald300}
            color={TOKENS.emerald700}
            customers={customers}
            employees={employees}
          />
        </Grid>
        <Grid item xs={6}>
          <TxnTable
            rows={expenses}
            amountKey="amount"
            label="Expenses (−)"
            total={totalExpenses}
            bg={TOKENS.red50}
            border={TOKENS.red300}
            color={TOKENS.red700}
            customers={customers}
            employees={employees}
          />
        </Grid>
      </Grid>
      <SubReportSummaryPdf
        points={{ count: points.length, value: totalPoints }}
        bonuses={{ count: bonuses.length, value: totalBonus }}
        raffles={{ count: raffles.length, value: totalRaffles }}
        expenses={{ count: expenses.length, value: totalExpenses }}
        ticketOut={{ count: ticketOuts.length, value: totalTickets }}
        total={totalOutgoing}
      />
      <ReconciliationPdf
        totalBank={totalBank}
        totalOutgoing={totalOutgoing}
        closingCash={closingCash}
        difference={difference}
        shortOver={shortOver}
        cashLeftEmployeeName={data.cashLeftEmployeeName}
      />
      <ManagerModificationsList
        auditLog={data.auditLog || []}
        customers={customers}
        employees={employees}
        manageBonusList={manageBonusList}
      />
    </Box>
  );
}

function SubReportSummaryPdf({
  points,
  bonuses,
  raffles,
  expenses,
  ticketOut,
  total,
}) {
  const cards = [
    {
      label: 'Points',
      count: points.count,
      value: points.value,
      color: TOKENS.blue700,
      bg: TOKENS.blue50,
    },
    {
      label: 'Bonuses',
      count: bonuses.count,
      value: bonuses.value,
      color: TOKENS.amber700,
      bg: TOKENS.amber50,
    },
    {
      label: 'Raffles',
      count: raffles.count,
      value: raffles.value,
      color: TOKENS.pink700,
      bg: TOKENS.pink50,
    },
    {
      label: 'Expenses',
      count: expenses.count,
      value: expenses.value,
      color: TOKENS.slate700,
      bg: TOKENS.slate100,
    },
    {
      label: 'Ticket-Out',
      count: ticketOut.count,
      value: ticketOut.value,
      color: TOKENS.violet700,
      bg: TOKENS.violet50,
    },
  ];
  return (
    <Box
      data-pdf-block="sub-report-summary"
      sx={{
        border: `1px solid ${TOKENS.slate200}`,
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          background: TOKENS.slate50,
          borderBottom: `1px solid ${TOKENS.slate200}`,
          px: 1,
          py: 0.75,
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: TOKENS.slate700,
          }}
        >
          Total Expenses Transactions
        </Typography>
      </Box>
      <Grid container spacing={0.5} sx={{ p: 1 }}>
        {cards.map((c) => (
          <Grid item xs={4} key={c.label}>
            <Box
              sx={{
                border: `1px solid ${c.bg}`,
                background: c.bg,
                borderRadius: 0.5,
                px: 1,
                py: 0.5,
              }}
            >
              <Typography sx={{ fontSize: 9, fontWeight: 700, color: c.color }}>
                {c.label} ({c.count})
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  fontFamily: 'monospace',
                  color: c.color,
                }}
              >
                {fmt(c.value)}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
      <Box
        sx={{
          borderTop: `1px solid ${TOKENS.slate200}`,
          background: TOKENS.slate50,
          px: 1,
          py: 0.75,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Typography sx={{ fontSize: 11, fontWeight: 700 }}>
          Total Expenses
        </Typography>
        <Typography
          sx={{ fontSize: 13, fontWeight: 700, fontFamily: 'monospace' }}
        >
          {fmt(total)}
        </Typography>
      </Box>
    </Box>
  );
}

function ReconciliationPdf({
  totalBank,
  totalOutgoing,
  closingCash,
  difference,
  shortOver,
  cashLeftEmployeeName,
}) {
  const cashLabel = cashLeftEmployeeName
    ? `Cash (${cashLeftEmployeeName})`
    : 'Cash (Closing Shift)';
  const shortOverColor = shortOver < 0 ? TOKENS.red700 : TOKENS.emerald700;
  const shortOverBg = shortOver < 0 ? TOKENS.red50 : TOKENS.emerald50;
  return (
    <Box
      data-pdf-block="reconciliation"
      sx={{
        border: `1px solid ${TOKENS.slate200}`,
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          background: TOKENS.slate50,
          borderBottom: `1px solid ${TOKENS.slate200}`,
          px: 1,
          py: 0.75,
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: TOKENS.slate700,
          }}
        >
          Reconciliation
        </Typography>
      </Box>
      <ReconRow label="Total Bank" value={fmt(totalBank)} />
      <ReconRow label="Total Expenses" value={fmt(totalOutgoing)} />
      <ReconRow
        label="Difference (Bank − Expenses)"
        value={fmt(difference)}
        bold
      />
      <ReconRow label={cashLabel} value={fmt(closingCash)} />
      <Box
        sx={{
          px: 1,
          py: 0.75,
          display: 'flex',
          justifyContent: 'space-between',
          background: shortOverBg,
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            color: shortOverColor,
          }}
        >
          Short / Over
        </Typography>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 700,
            fontFamily: 'monospace',
            color: shortOverColor,
          }}
        >
          {shortOver > 0 ? '+' : ''}
          {fmt(shortOver)}
        </Typography>
      </Box>
    </Box>
  );
}

function ReconRow({ label, value, bold }) {
  return (
    <Box
      sx={{
        px: 1,
        py: 0.5,
        display: 'flex',
        justifyContent: 'space-between',
        background: bold ? TOKENS.slate50 : 'transparent',
        borderBottom: `1px solid ${TOKENS.slate100}`,
      }}
    >
      <Typography
        sx={{
          fontSize: 11,
          color: TOKENS.slate700,
          fontWeight: bold ? 700 : 400,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 11,
          fontFamily: 'monospace',
          fontWeight: bold ? 700 : 600,
          color: TOKENS.slate900,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function TxnTable({
  rows = [],
  amountKey,
  label,
  total,
  bg,
  border,
  color,
  customers,
  employees,
}) {
  return (
    <Box sx={{ border: `1px solid ${border}`, borderRadius: 1 }}>
      <Box
        sx={{
          background: bg,
          borderBottom: `1px solid ${border}`,
          px: 1,
          py: 0.75,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            color,
          }}
        >
          {label}
        </Typography>
        <Typography sx={{ fontSize: 10, color }}>
          {rows.length} entries
        </Typography>
      </Box>
      {rows.map((r, i) => (
        <Box
          key={i}
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 1,
            py: 0.5,
            borderBottom: `1px solid ${TOKENS.slate100}`,
            background: r.modified ? bg + '80' : 'transparent',
          }}
        >
          <Typography
            sx={{
              fontSize: 9,
              fontFamily: 'monospace',
              color: TOKENS.slate500,
              width: 20,
            }}
          >
            {i + 1}
          </Typography>
          <Typography sx={{ fontSize: 10, flex: 1 }} noWrap>
            {enrichTxnDescription(r, customers, employees) || r.desc}
            {r.modified && (
              <span style={{ color, fontWeight: 700, marginLeft: 4 }}>★</span>
            )}
          </Typography>
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              fontFamily: 'monospace',
              color,
            }}
          >
            {fmt(r[amountKey] || 0)}
          </Typography>
        </Box>
      ))}
      <Box
        sx={{
          background: bg,
          borderTop: `2px solid ${border}`,
          px: 1,
          py: 0.75,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase',
            color,
          }}
        >
          Total
        </Typography>
        <Typography
          sx={{ fontSize: 13, fontWeight: 700, fontFamily: 'monospace', color }}
        >
          {fmt(total)}
        </Typography>
      </Box>
    </Box>
  );
}

// ── BONUS ────────────────────────────────────────────────────────────────
function BonusPdf({ data }) {
  const bonuses = (data.bonuses || []).filter((b) => b.status !== 'deleted');
  const total = bonuses.reduce((s, b) => s + Number(b.bonusAmount || 0), 0);
  const decisions = data.decisions || [];
  const decisionMap = {};
  decisions.forEach((d) => {
    decisionMap[d.entityId] = d.decision;
  });
  const approved = decisions.filter((d) => d.decision === 'APPROVED').length;
  const flagged = decisions.filter((d) => d.decision === 'FLAGGED').length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Grid container spacing={1}>
        <Grid item xs={3}>
          <Kpi
            label="Total Amount"
            value={fmtMoney(total)}
            accent={TOKENS.amber200}
          />
        </Grid>
        <Grid item xs={3}>
          <Kpi
            label="Total Bonuses"
            value={bonuses.length}
            accent={TOKENS.slate200}
          />
        </Grid>
        <Grid item xs={3}>
          <Kpi label="Approved" value={approved} accent={TOKENS.emerald200} />
        </Grid>
        <Grid item xs={3}>
          <Kpi label="Flagged" value={flagged} accent={TOKENS.red200} />
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        {bonuses.map((b) => {
          const c = bonusGameColor(b.bonusName);
          const dec = decisionMap[b.bonusId];
          // Match the on-screen BonusDetail which falls back to b.photo
          // when imageUrl isn't set — the backend uses either field
          // depending on how the bonus was created.
          const photoUrl = b.imageUrl || b.photo;
          return (
            <Grid item xs={6} key={b.bonusId}>
              <Box
                sx={{
                  border: `1px solid ${
                    dec === 'FLAGGED' ? TOKENS.red300 : TOKENS.slate200
                  }`,
                  borderRadius: 1,
                  p: 1,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 1,
                    mb: 0.5,
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    {/* Game-type tag rendered as a plain Box+text — MUI
                        Chip's nested flex layout sometimes drops its label
                        when html2canvas captures it at this tiny size. */}
                    {b.bonusName && (
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-block',
                          px: 0.75,
                          py: 0.125,
                          background: c.bg,
                          color: c.text,
                          border: `1px solid ${c.border || c.text}`,
                          borderRadius: 0.5,
                          fontSize: 9,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          lineHeight: 1.4,
                        }}
                      >
                        {b.bonusName}
                      </Box>
                    )}
                    <Typography
                      sx={{ fontSize: 11, fontWeight: 600, mt: 0.5 }}
                      noWrap
                    >
                      {b.firstName} {b.lastName}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 10, color: TOKENS.slate500 }}
                      noWrap
                    >
                      {b.specify}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 700,
                      fontFamily: 'monospace',
                      color: TOKENS.amber700,
                    }}
                  >
                    ${b.bonusAmount}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    aspectRatio: '3/4',
                    background: '#000',
                    border: `2px solid ${TOKENS.amber200}`,
                    borderRadius: 0.5,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {photoUrl && (
                    <Box
                      component="img"
                      src={pdfSrc(photoUrl)}
                      alt=""
                      sx={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                        display: 'block',
                      }}
                      crossOrigin="anonymous"
                    />
                  )}
                </Box>
                <Typography
                  sx={{
                    fontSize: 9,
                    color: TOKENS.slate500,
                    mt: 0.5,
                    textAlign: 'center',
                  }}
                >
                  Machine #{b.machine} · {b.time}
                </Typography>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

// ── RAFFLE ───────────────────────────────────────────────────────────────
function RafflePdf({ data }) {
  const raffles = (data.raffles || []).filter((r) => r.status !== 'deleted');
  const total = raffles.reduce((s, r) => s + Number(r.amount || 0), 0);
  const decisions = data.decisions || [];
  const decisionMap = {};
  decisions.forEach((d) => {
    decisionMap[d.entityId] = d.decision;
  });
  const approved = decisions.filter(
    (d) => d.entityType === 'RAFFLE' && d.decision === 'APPROVED'
  ).length;
  const flagged = decisions.filter(
    (d) => d.entityType === 'RAFFLE' && d.decision === 'FLAGGED'
  ).length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Grid container spacing={1}>
        <Grid item xs={4}>
          <Kpi
            label="Total Amount"
            value={fmtMoney(total)}
            accent={TOKENS.pink200}
          />
        </Grid>
        <Grid item xs={4}>
          <Kpi label="Approved" value={approved} accent={TOKENS.emerald200} />
        </Grid>
        <Grid item xs={4}>
          <Kpi label="Flagged" value={flagged} accent={TOKENS.red200} />
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        {raffles.map((r) => (
          <Grid item xs={6} key={r.raffleId}>
            <Box
              sx={{
                border: `1px solid ${
                  decisionMap[r.raffleId] === 'FLAGGED'
                    ? TOKENS.red300
                    : TOKENS.slate200
                }`,
                borderRadius: 1,
                p: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 1,
                  mb: 0.5,
                }}
              >
                <Typography
                  sx={{ fontSize: 11, fontWeight: 600, flex: 1 }}
                  noWrap
                >
                  {r.name}
                </Typography>
                {r.winnerNumber && (
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      fontFamily: 'monospace',
                      color: TOKENS.pink700,
                      background: TOKENS.pink50,
                      border: `1px solid ${TOKENS.pink200}`,
                      borderRadius: 0.5,
                      px: 0.5,
                    }}
                  >
                    # {r.winnerNumber}
                  </Typography>
                )}
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    fontFamily: 'monospace',
                    color: TOKENS.pink700,
                  }}
                >
                  ${r.amount}
                </Typography>
              </Box>
              <Grid container spacing={0.5}>
                <Grid item xs={6}>
                  <Box
                    sx={{
                      aspectRatio: '3/4',
                      background: '#000',
                      border: `2px solid ${TOKENS.blue200}`,
                      borderRadius: 0.5,
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {r.photo?.[0]?.localURL && (
                      <Box
                        component="img"
                        src={pdfSrc(r.photo[0].localURL)}
                        alt="ID"
                        sx={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          width: 'auto',
                          height: 'auto',
                          objectFit: 'contain',
                          display: 'block',
                        }}
                        crossOrigin="anonymous"
                      />
                    )}
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box
                    sx={{
                      aspectRatio: '3/4',
                      background: '#000',
                      border: `2px solid ${TOKENS.emerald200}`,
                      borderRadius: 0.5,
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {r.files?.[0]?.localURL && (
                      <Box
                        component="img"
                        src={pdfSrc(r.files[0].localURL)}
                        alt="VAL"
                        sx={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          width: 'auto',
                          height: 'auto',
                          objectFit: 'contain',
                          display: 'block',
                        }}
                        crossOrigin="anonymous"
                      />
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

// ── POINTS ───────────────────────────────────────────────────────────────
function PointsPdf({ data }) {
  const points = data.points || [];
  const total = points.reduce((s, p) => s + Number(p.points || 0), 0);
  const half = Math.ceil(points.length / 2);
  const col1 = points.slice(0, half);
  const col2 = points.slice(half);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Grid container spacing={1}>
        <Grid item xs={4}>
          <Kpi
            label="Total Points"
            value={fmtMoney(total)}
            accent={TOKENS.emerald200}
          />
        </Grid>
        <Grid item xs={4}>
          <Kpi
            label="Total Matches"
            value={points.length}
            accent={TOKENS.slate200}
          />
        </Grid>
        <Grid item xs={4}>
          <Kpi
            label="Customers"
            value={new Set(points.map((p) => p.customerId)).size}
            accent={TOKENS.slate200}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        {[col1, col2].map((col, idx) => (
          <Grid item xs={6} key={idx}>
            <Box
              sx={{ border: `1px solid ${TOKENS.slate200}`, borderRadius: 1 }}
            >
              {col.map((p, i) => {
                const t = pointTier(p.points);
                return (
                  <Box
                    key={i}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 1,
                      py: 0.5,
                      borderBottom: `1px solid ${TOKENS.slate100}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 4,
                        height: 12,
                        background: t.dot,
                        borderRadius: 0.5,
                        mr: 1,
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 10, fontWeight: 600 }} noWrap>
                        {p.customer}
                      </Typography>
                    </Box>
                    {/* Machine pill rendered as plain Box + SvgIcon — MUI
                        Chip's nested flex layout sometimes drops its label
                        when html2canvas captures it at this tiny size. */}
                    {p.machine && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.375,
                          background: TOKENS.blue50,
                          border: `1px solid ${TOKENS.blue200}`,
                          borderRadius: 999,
                          px: 0.75,
                          py: 0.125,
                          mr: 0.75,
                        }}
                      >
                        <DesktopWindowsIcon
                          sx={{ fontSize: 10, color: TOKENS.blue700 }}
                        />
                        <Typography
                          sx={{
                            fontSize: 9,
                            fontWeight: 700,
                            fontFamily: 'monospace',
                            color: TOKENS.blue700,
                            lineHeight: 1.2,
                          }}
                        >
                          #{p.machine}
                        </Typography>
                      </Box>
                    )}
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        fontFamily: 'monospace',
                        color: t.color,
                        width: 40,
                        textAlign: 'right',
                      }}
                    >
                      ${p.points}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

// ── TICKET OUT ───────────────────────────────────────────────────────────
function TicketPdf({ data }) {
  const ticketOuts = (data.ticketOuts || []).filter(
    (t) => t.status !== 'deleted'
  );
  const byMachine = {};
  ticketOuts.forEach((t) => {
    const key = t.machine;
    if (!byMachine[key])
      byMachine[key] = { machine: key, tickets: [], game: t.game || 'POG' };
    byMachine[key].tickets.push({ amt: Number(t.ticketOut), time: t.time });
  });
  const machines = Object.values(byMachine)
    .map((m) => ({ ...m, total: m.tickets.reduce((s, t) => s + t.amt, 0) }))
    .sort((a, b) => b.total - a.total);
  const total = machines.reduce((s, m) => s + m.total, 0);
  const ticketCount = ticketOuts.length;

  const half = Math.ceil(machines.length / 2);
  const col1 = machines.slice(0, half);
  const col2 = machines.slice(half);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Grid container spacing={1}>
        <Grid item xs={3}>
          <Kpi
            label="Total"
            value={fmtMoney(total)}
            accent={TOKENS.violet200}
          />
        </Grid>
        <Grid item xs={3}>
          <Kpi label="Tickets" value={ticketCount} accent={TOKENS.slate200} />
        </Grid>
        <Grid item xs={3}>
          <Kpi
            label="Machines"
            value={machines.length}
            accent={TOKENS.slate200}
          />
        </Grid>
        <Grid item xs={3}>
          <Kpi
            label="Highest"
            value={fmtMoney(
              Math.max(
                0,
                ...machines.flatMap((m) => m.tickets.map((t) => t.amt))
              )
            )}
            accent={TOKENS.amber200}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        {[col1, col2].map((col, idx) => (
          <Grid item xs={6} key={idx}>
            <Box
              sx={{ border: `1px solid ${TOKENS.slate200}`, borderRadius: 1 }}
            >
              {col.map((m) => {
                const c = ticketGameColor(m.game);
                return (
                  <Box
                    key={m.machine}
                    sx={{
                      px: 1,
                      py: 0.5,
                      borderBottom: `1px solid ${TOKENS.slate100}`,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 0.25,
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <Typography sx={{ fontSize: 10, fontWeight: 700 }}>
                          M{m.machine}
                        </Typography>
                        <Chip
                          size="small"
                          label={m.game}
                          sx={{
                            height: 14,
                            fontSize: 8,
                            background: c.bg,
                            color: c.text,
                          }}
                        />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: 10,
                          fontWeight: 700,
                          fontFamily: 'monospace',
                        }}
                      >
                        ${fmt(m.total)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {m.tickets.map((t, i) => (
                        <Box key={i}>
                          <Typography
                            sx={{
                              fontSize: 10,
                              fontWeight: 700,
                              fontFamily: 'monospace',
                            }}
                          >
                            ${fmt(t.amt)}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 8,
                              color: TOKENS.slate500,
                              fontFamily: 'monospace',
                            }}
                          >
                            {t.time}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
